import { useQuery, useQueryClient } from '@tanstack/react-query';
import Image from 'next/legacy/image';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// Components
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { Modal } from '@startengine/design-system.ui.modal';

// Utils

// Services

// Styles
import { colors } from '@startengine/design-system.theme.main';
import RewardTile from './RewardsTile';
import { OwnersBox } from './styles';
import { getReservationsByUserId } from 'services/reservations';
import { imagePath } from 'utils/helpers';

const AllRewardsModal = ({ open = false, handleClose = () => null }) => {
  const BONUS_TEXT = 'Venture Club';

  const queryClient = useQueryClient();
  const rewardData = queryClient.getQueryData(['rewards']);
  const offeringData = queryClient.getQueryData(['offeringData']);
  const primaryUserId = useSelector((state) => state.user.primaryUserId);
  const isTTW = offeringData && offeringData.status === 'testing';

  const { data: reservations } = useQuery({
    queryKey: ['reservations', primaryUserId],
    queryFn: () =>
      getReservationsByUserId({
        primaryUserId,
        pageParam: 0,
        size: 10000,
        statuses: 'CONVERTED,APPROVED',
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    staleTime: 5 * (60 * 1000),
    enabled: Boolean(primaryUserId),
  });

  // get the first instance of a reservation that matches the slug
  const hasReservationForTTW = useMemo(() => {
    if (!reservations?.list) {
      return null;
    }

    return reservations?.list?.find((res) => res?.offering?.slug === offeringData?.slug);
  }, [offeringData?.slug, reservations?.list]);

  const filteredRewardData =
    rewardData !== undefined
      ? rewardData
          ?.map((obj) => {
            if (!isTTW && !hasReservationForTTW && obj.forTtwOnly === true) {
              return null;
            }

            return obj;
          })
          .filter((obj) => obj !== null)
      : [];

  const sortedRewardData = filteredRewardData?.sort(
    (a, b) => parseFloat(a.minimumAmount) - parseFloat(b.minimumAmount),
  );

  return (
    <Modal open={open} onClose={handleClose} fullPage sx={{ '.modal-footer': { display: 'none' } }}>
      <Grid container item xs={12}>
        <OwnersBox>
          <Image
            src={`${imagePath}/images/offering/owner-bonus.svg`}
            alt={BONUS_TEXT || 'Owners Bonus'}
            width={50}
            height={50}
          />
          <Grid item>
            <Typography
              variant="h7"
              sx={{
                color: colors.SEDark,
                display: 'block',
              }}
            >
              Stack {BONUS_TEXT} & Rewards!
            </Typography>
            <Typography
              variant="p"
              sx={{
                color: colors.gray1,
              }}
            >
              Members get an extra 10%{' '}
              {['convertible_note', 'debt', 'safe'].includes(offeringData?.offeringType)
                ? 'interest rate'
                : 'shares'}{' '}
              in addition to rewards below!
            </Typography>
          </Grid>
        </OwnersBox>
        <Grid
          item
          mt={4}
          mb={2}
          xs={12}
          flexDirection="column"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item container justifyContent="space-between" mb={3}>
            <Grid
              item
              xs={2}
              md={8}
              sx={{
                my: '0 !important',
              }}
              maxWidth="none"
            >
              <Typography variant="h4">REWARDS</Typography>
            </Grid>
            <Typography
              variant="p"
              sx={{
                color: colors.gray1,
              }}
            >
              All perks occur or are delivered after the offering is completed.
            </Typography>
          </Grid>
          <Grid item container xs={12}>
            {sortedRewardData?.map((reward) => (
              <RewardTile reward={reward} key={reward?.title} />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default AllRewardsModal;

AllRewardsModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};
